import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Single-image product gallery with a scroll-snap track, an "01 — 03" counter and
 * optional previous/next buttons. Follows the selected variant's featured media.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track - Horizontally scrolling slide container.
 * @property {HTMLElement[]} [slides] - One per image, with `data-media-id`.
 * @property {HTMLElement} [counter] - Current slide number.
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
class ProductShowcase extends Component {
  requiredRefs = ['track'];

  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {AbortController | undefined} */
  #abortController;

  #index = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, {
      signal: this.#abortController.signal,
    });

    this.#observe();

    const start = this.refs.slides?.findIndex((slide) => slide.hasAttribute('data-selected')) ?? -1;
    if (start > 0) {
      this.#goTo(start, 'instant');
    } else if ((this.refs.slides?.length ?? 0) > 1) {
      this.#setIndex(0);
    }
  }

  updatedCallback() {
    super.updatedCallback();
    this.#observe();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#observer?.disconnect();
  }

  showPrevious() {
    this.#goTo(this.#index - 1);
  }

  showNext() {
    this.#goTo(this.#index + 1);
  }

  #observe() {
    this.#observer?.disconnect();
    const slides = this.refs.slides ?? [];
    if (slides.length < 2) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.#setIndex(slides.indexOf(/** @type {HTMLElement} */ (entry.target)));
        }
      },
      { root: this.refs.track, threshold: 0.6 }
    );

    for (const slide of slides) this.#observer.observe(slide);
  }

  /**
   * @param {number} index
   * @param {ScrollBehavior} [behavior]
   */
  #goTo(index, behavior) {
    const slides = this.refs.slides ?? [];
    if (!slides.length) return;

    const target = (index + slides.length) % slides.length;
    const slide = slides[target];
    if (!slide) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.refs.track.scrollTo({
      left: slide.offsetLeft,
      behavior: behavior ?? (reduceMotion ? 'instant' : 'smooth'),
    });
    this.#setIndex(target);
  }

  /**
   * @param {number} index
   */
  #setIndex(index) {
    if (index < 0) return;

    this.#index = index;
    if (this.refs.counter) this.refs.counter.textContent = String(index + 1).padStart(2, '0');

    for (const [i, slide] of (this.refs.slides ?? []).entries()) {
      slide.toggleAttribute('inert', i !== index);
    }
  }

  /**
   * Scrolls to the newly selected variant's image.
   * @param {Event & { promise?: Promise<any> }} event
   */
  #onProductSelect = (event) => {
    event.promise
      ?.then(({ detail }) => {
        const mediaId = detail?.resource?.featured_media?.id;
        if (!mediaId) return;

        const index = this.refs.slides?.findIndex((slide) => slide.dataset.mediaId === String(mediaId)) ?? -1;
        if (index >= 0) this.#goTo(index);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-showcase] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-showcase')) {
  customElements.define('product-showcase', ProductShowcase);
}
